import React, {FC, useMemo, useState} from 'react';
import {
  FlatList,
  LayoutChangeEvent,
  TouchableOpacity,
  View,
} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import styled from 'styled-components/native';
import Animated, {
  Extrapolation,
  FadeInUp,
  FadeOutUp,
  interpolate,
  runOnJS,
  useAnimatedReaction,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  ZoomIn,
  ZoomOut,
} from 'react-native-reanimated';
import {wdp} from '../../../utils/responsive';
import HeaderSearchInput from '../headerSearchInput';
import {useTheme} from 'styled-components';
import {Theme} from '../../../theme/lightTheme';
import Icon from '../../icon';
import TextGeneric from '../../textGeneric';
import {NavigationProp, useNavigation} from '@react-navigation/native';
import {getFilterItemByKey} from '../../../utils/helpers';
import Chip from '../../chip';
import {HeaderSearchProps} from '../../../interfaces/components/headerSearch';
import BackIcon from '../backIcon';
import MenuIcon from '../menuIcon';
import {useCustomHeaderHeight} from '../../../utils/hooks/useHeaderHeight';
import GenericButton from '../../button';

const SCROLL_VALUE = 50;
const SCREEN_WIDTH = wdp(100);

const Container = styled(Animated.View)<{
  headerHeight: number;
  topInset: number;
}>`
  z-index: 2;
  height: ${({headerHeight}) => headerHeight}px;
  justify-content: flex-end;
  background-color: ${({theme}) => theme.homeBackground};
`;

const HeaderContentContainer = styled(Animated.View)<{topInset: number}>`
  top: 0;
  flex-direction: row;
  width: 100%;
  align-self: center;
  position: absolute;
  align-items: flex-start;
  justify-content: space-between;
  border-top-right-radius: 30px;
  border-top-left-radius: 30px;
  background-color: ${({theme}) => theme.headerBackground};
  padding-top: ${({theme}) => theme.text.s8}px;
  margin-top: ${({topInset}) => topInset}px;
`;
const RightContainer = styled(Animated.View)`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding-right: 2%;
`;
const FilterIconContainer = styled(Animated.View)<{disabled?: boolean}>`
  align-items: center;
  justify-content: center;
  opacity: ${({disabled}) => (disabled ? 0.7 : 1)};
`;
const FilterBadgeContainer = styled(Animated.View)`
  position: absolute;
  right: 0;
  bottom: 0;
  width: ${({theme}) => theme.text.s7}px;
  border-radius: 100px;
  aspect-ratio: 1;
  align-items: center;
  justify-content: center;
  background-color: ${({theme}) => theme.primary + '22'};
`;
const FilterBadge = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s11}px;
  color: ${({theme}) => theme.secondary};
  text-align: center;
`;
const HorizontalSeparator = styled.View`
  width: ${wdp(3)}px;
`;
const IconContainer = styled.View`
  height: 100%;
  align-items: center;
  justify-content: flex-start;
  align-self: center;
  padding-top: 2%;
`;

const HeaderSearch: FC<HeaderSearchProps> = ({
  scrollY,
  onFilterPress,
  filters,
  filtersResult,
  setFiltersResult,
  isSearchHidden,
  isFiltersHidden,
  renderAdditionalFilters,
  onPublisherPress,
  showPublisherIcon,
  publisherActiveFilter,
  publisherActiveName,
  onPublisherClear,
  renderRightIcon,
  ...props
}) => {
  const theme = useTheme() as Theme;
  const navigation = useNavigation<NavigationProp<any>>();
  const {top: topInset} = useSafeAreaInsets();
  const headerHeight = useCustomHeaderHeight();
  const [additionalFiltersHeight, setAdditionalFiltersHeight] = useState(0);
  const [isPointerEventsEnabled, setIsPointerEventsEnabled] = useState(false);
  const filtersNum = useMemo(
    () =>
      Object.keys(filtersResult || {}).filter(
        item =>
          !!getFilterItemByKey(filters || [], item, filtersResult?.[item]),
      ).length,
    [filtersResult],
  );

  const filtersNumWithPublisher = useMemo(
    () => filtersNum + (filtersResult?.publisher ? 1 : 0),
    [filtersNum],
  );

  const inputOpacity = useSharedValue(0);
  useAnimatedReaction(
    () => scrollY.value,
    () => {
      inputOpacity.value = withTiming(0);
    },
  );
  const headerAnimatedStyle = useAnimatedStyle(() => {
    const animatedHeight = interpolate(
      scrollY.value,
      [0, headerHeight - (topInset || 0)],
      [
        (headerHeight - (topInset || 0)) *
          (filtersNumWithPublisher > 0 ? 1.6 : 1) +
          additionalFiltersHeight,
        headerHeight - (topInset || 0),
      ],
      Extrapolation.CLAMP,
    );
    return {
      height: animatedHeight,
    };
  });

  const iconContainerAStyle = useAnimatedStyle(() => {
    const translateY = interpolate(
      scrollY.value,
      [0, SCROLL_VALUE],
      [(headerHeight - (topInset || 0)) * 0.9, 0],
      Extrapolation.CLAMP,
    );
    const opacity = interpolate(
      scrollY.value,
      [0, SCROLL_VALUE / 2.2],
      [1, 0],
      Extrapolation.CLAMP,
    );

    // Update pointer events based on translateY
    const shouldEnablePointerEvents =
      translateY < (headerHeight - (topInset || 0)) * 0.9;
    runOnJS(setIsPointerEventsEnabled)(shouldEnablePointerEvents);

    return {
      position: 'absolute',
      zIndex: 99,
      flexDirection: 'column',
      transform: [{translateY: translateY}],
      opacity,
      width: SCREEN_WIDTH,
    };
  });

  const logoContainerStyle = useAnimatedStyle(() => {
    const opacity = interpolate(
      scrollY.value,
      [0, SCROLL_VALUE * 1.5],
      [0, 1],
      Extrapolation.CLAMP,
    );
    const scale = interpolate(
      inputOpacity.value,
      [0, 1],
      [1, 0],
      Extrapolation.CLAMP,
    );

    return {
      zIndex: 0,
      position: 'absolute',
      alignSelf: 'center',
      alignItems: 'center',
      justifyContent: 'center',
      opacity,
      transform: [{scale}],
      bottom: 0,
      width: SCREEN_WIDTH,
      height: '100%',
    };
  });
  const closeButtonAnimatedStyle = useAnimatedStyle(() => {
    return {
      position: 'absolute',
      right: theme.text.s55,
      bottom: '55%',
      opacity: inputOpacity.value,
      transform: [{scale: inputOpacity.value}],
    };
  });

  const publisherIconStyle = useAnimatedStyle(() => {
    const opacity = interpolate(
      scrollY.value,
      [0, SCROLL_VALUE * 1.5],
      [1, 0],
      Extrapolation.CLAMP,
    );
    const scale = interpolate(
      scrollY.value,
      [0, SCROLL_VALUE * 4],
      [1, 0],
      Extrapolation.CLAMP,
    );
    return {
      opacity,
      transform: [{scale}],
    };
  });
  const filterIconAnimatedStyle = useAnimatedStyle(() => {
    const opacity = interpolate(
      inputOpacity.value,
      [0, 1],
      [1, 0],
      Extrapolation.CLAMP,
    );
    const scale = interpolate(
      inputOpacity.value,
      [0, 1],
      [1, 0],
      Extrapolation.CLAMP,
    );
    return {
      opacity,
      transform: [{scale}],
    };
  });
  const handleSearchPress = () => {
    if (scrollY.value >= SCROLL_VALUE) {
      inputOpacity.value = withTiming(1);
    }
  };
  const _renderFilters = ({item}: {item: string}) => {
    const res = getFilterItemByKey(filters || [], item, filtersResult?.[item]);
    if (!res) {
      return null;
    }

    return (
      <Chip
        onPress={() => {
          setFiltersResult &&
            setFiltersResult((prev: any) => {
              return {...prev, [item]: ''};
            });
          if (item === 'publisher' && onPublisherClear) {
            onPublisherClear();
          }
        }}
        label={
          res?.filterData?.label +
          ': ' +
          (item === 'publisher' ? publisherActiveName : res?.filterItem?.value)
        }
        isLabelGradient
        showRightIcon
        rightIconName={'close'}
        rightIconType={'MaterialIcons'}
        rightIconContainerStyle={{
          position: 'relative',
          right: 10,
        }}
        style={{opacity: 0.95}}
        selected={false}
      />
    );
  };

  return (
    <Container
      topInset={topInset || 0}
      headerHeight={headerHeight - (topInset || 0)}>
      <HeaderContentContainer
        topInset={topInset || 0}
        style={headerAnimatedStyle}>
        <Animated.View style={{paddingLeft: '2%'}}>
          {navigation?.canGoBack() ? (
            <BackIcon tintColor={theme.header.primary} canGoBack />
          ) : (
            <Icon
              name={'logo'}
              color={theme.header.primary}
              size={theme.text.s3}
            />
          )}
        </Animated.View>
        {isSearchHidden ? (
          <IconContainer>
            <Icon
              name={'genio'}
              color={theme.header.primary}
              width={theme.text.s3 * 3.75}
              height={theme.text.s3}
            />
          </IconContainer>
        ) : (
          <HeaderSearchInput
            onSearchPress={handleSearchPress}
            externalOpacity={inputOpacity}
            scrollY={scrollY}
            {...props}
          />
        )}
        {!isSearchHidden && (
          <Animated.View pointerEvents={'none'} style={logoContainerStyle}>
            <IconContainer>
              <Icon
                name={'genio'}
                color={theme.header.primary}
                width={theme.text.s3 * 3.75}
                height={theme.text.s3}
              />
            </IconContainer>
          </Animated.View>
        )}
        {(showPublisherIcon || !isFiltersHidden) && (
          <Animated.View
            pointerEvents={isPointerEventsEnabled ? 'none' : 'auto'}
            style={iconContainerAStyle}>
            {(showPublisherIcon || !isFiltersHidden) &&
              filtersNumWithPublisher > 0 && (
                <Animated.View
                  entering={FadeInUp}
                  exiting={FadeOutUp.duration(100)}>
                  <FlatList
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    data={Object.keys(filtersResult || {})}
                    renderItem={_renderFilters}
                    ItemSeparatorComponent={HorizontalSeparator}
                    contentContainerStyle={{
                      paddingStart: '5%',
                      flexGrow: 1,
                    }}
                  />
                </Animated.View>
              )}
            {showPublisherIcon && (
              <Animated.View
                style={{flex: 1, zIndex: 100}}
                onLayout={(event: LayoutChangeEvent) => {
                  const {height} = event.nativeEvent.layout;
                  setAdditionalFiltersHeight(height);
                }}>
                {renderAdditionalFilters && renderAdditionalFilters()}
              </Animated.View>
            )}
          </Animated.View>
        )}
        <RightContainer style={filterIconAnimatedStyle}>
          {showPublisherIcon && (
            <FilterIconContainer
              style={publisherIconStyle}
              pointerEvents={props.isLoading ? 'none' : 'auto'}
              disabled={props.isLoading}>
              <Icon
                name={'publisher'}
                size={theme.text.s3}
                color={theme.header.primary}
                role={'button'}
                onPress={onPublisherPress}
              />
              {!!publisherActiveFilter && (
                <FilterBadgeContainer entering={ZoomIn} exiting={ZoomOut}>
                  <FilterBadge>{publisherActiveFilter || ''}</FilterBadge>
                </FilterBadgeContainer>
              )}
            </FilterIconContainer>
          )}
          {!isFiltersHidden ? (
            <FilterIconContainer
              pointerEvents={props.isLoading ? 'none' : 'auto'}
              disabled={props.isLoading}>
              <Icon
                name={'filterSolid'}
                size={theme.text.s3}
                color={theme.header.primary}
                role={'button'}
                onPress={onFilterPress}
              />
              {(filtersNum || 0) - (filtersResult?.publisher ? 1 : 0) > 0 && (
                <FilterBadgeContainer entering={ZoomIn} exiting={ZoomOut}>
                  <FilterBadge>
                    {filtersNum - (filtersResult?.publisher ? 1 : 0)}
                  </FilterBadge>
                </FilterBadgeContainer>
              )}
            </FilterIconContainer>
          ) : showPublisherIcon ? null : renderRightIcon ? (
            renderRightIcon()
          ) : // @ts-ignore
          navigation?.toggleDrawer ? (
            <MenuIcon tintColor={theme.header.primary} canGoBack />
          ) : isFiltersHidden ? (
            <View
              style={{
                alignItems: 'center',
                justifyContent: 'center',
                height: theme.text.s1,
                aspectRatio: 1,
              }}>
              <Icon
                name={'logo'}
                color={theme.header.primary}
                size={theme.text.s3}
              />
            </View>
          ) : (
            <View style={{width: theme.text.s1, aspectRatio: 1}} />
          )}
        </RightContainer>
        <Animated.View style={closeButtonAnimatedStyle}>
          <TouchableOpacity
            onPress={() => {
              inputOpacity.value = withTiming(0, {duration: 200});
            }}>
            <Icon
              name={'x'}
              color={theme.header.primary}
              size={theme.text.s55}
            />
          </TouchableOpacity>
        </Animated.View>
      </HeaderContentContainer>
    </Container>
  );
};

export default HeaderSearch;
